import React, { useMemo } from "react";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
 label?: string | React.ReactNode;
 error?: string;
 helperText?: string;
 fullWidth?: boolean;
}

// Counter for generating stable IDs when id prop is not provided
let inputIdCounter = 0;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(({
 label,
 error,
 helperText,
 fullWidth = false,
 className = "",
 id,
 ...props
}, ref) => {
 // Generate stable ID only once using useMemo
 const inputId = useMemo(() => {
  if (id) return id;
  // Use a stable counter-based ID instead of random
  return `input-${++inputIdCounter}`;
 }, [id]);
 const hasError = !!error;

 const inputClasses = `
  block w-full px-3 py-2 border 
  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
  disabled:bg-gray-100 disabled:cursor-not-allowed
  ${hasError ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"}
  ${fullWidth ? "w-full" : ""}
  ${className}
 `.trim().replace(/\s+/g, " ");

 return (
  <div className={fullWidth ? "w-full" : ""}>
   {label && (
    <label
     htmlFor={inputId}
     className="block text-sm font-medium text-gray-700 mb-1"
    >
     {label}
    </label>
   )}
   <input
    ref={ref}
    id={inputId}
    className={inputClasses}
    aria-invalid={hasError}
    aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
    {...props}
   />
   {error && (
    <p id={`${inputId}-error`} className="mt-1 text-sm text-red-600" role="alert">
     {error}
    </p>
   )}
   {helperText && !error && (
    <p id={`${inputId}-helper`} className="mt-1 text-sm text-gray-500">
     {helperText}
    </p>
   )}
  </div>
 );
});

Input.displayName = "Input";

export default Input;

